<template>
  <div class="citation uk-flex uk-flex-nowrap uk-flex-between uk-width-1-1 uk-position-relative">
    <span
      class="citation-link uk-flex uk-flex-nowrap uk-flex-row uk-link uk-link-text uk-display-inline-block uk-text-left"
      :uk-tooltip="citationLinkProps.tooltip"
      @click="citationLinkProps.click()"
    >
      <div class="uk-flex">
        <span class="citation-title-icon">
          <span v-if="getDisputedCitation" uk-icon="comment"></span>
          <span
            v-if="isFile && !getDisputedCitation"
            uk-icon="icon: folder; ratio: 1.3"
            style="color: black"
          ></span>
          <span
            v-if="islink && !getDisputedCitation && !isCite"
            uk-icon="icon: link; ratio: 1.3"
            style="color: black"
          ></span>
          <span
            v-if="!isFile && !islink && !isCite && !getDisputedCitation"
            uk-icon="icon: file-text; ratio: 1.3"
            style="color: black"
          ></span>
          <span v-if="isCite && !getDisputedCitation">
            <img src="/icons/debate-library.svg" height="26px" width="26px" alt="" />
          </span>
        </span>
        <span class="citation-title">{{ citation.title }}</span>
      </div>
    </span>
    <span v-if="isCitationEditable()">
      <span
        class="citation-remove-btn uk-text-center"
        :class="{ 'outline-citation-ban': outlineCitation }"
        uk-icon="ban"
        @click="removeCitationClicked()"
        uk-tooltip="Remove citation"
      ></span>
    </span>
  </div>
</template>

<script>
import PermissionLevel from '@/assets/js/enum/PermissionLevel';
import RoundStatus from '@/assets/js/enum/RoundStatus';
import { EventBus } from '@/assets/js/event-bus';
import { mapGetters, mapState } from 'vuex';
import { getId } from '@/assets/js/utils';
import { url } from '@vuelidate/validators';

export default {
  name: 'CitationRow',

  props: {
    citation: Object,
    parent: Object,
    parentCounterpoint: Object,
    parentAuthor: Object,
    outlineCitation: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState(['documentData', 'publicView', 'currentTeam', 'debateNextState', 'userData']),

    ...mapGetters([
      'userId',
      'teamID',
      'roundPublished',
      'myLatestRound',
      'myLatestRoundNumber',
      'myLatestRoundActive',
      'teamMemberData',
      'roundStatus',
      'permissionLevel',
    ]),

    getDisputedCitation() {
      if (
        this.userData?.id !== this.citation?.disputedBy &&
        this.citation?.disputed &&
        this.citation?.childPublished
      ) {
        return true;
      } else if (this.userData?.id === this.citation?.disputedBy && this.citation?.disputed) {
        return true;
      } else return false;
    },
    /** The current user */
    isCurrentAuthor() {
      return getId(this.parent.author) === this.userId;
    },

    /** This annotation belongs to the currently active round.
     * (If the annotation is in host instruction mode [isHostInstructionMode], no rounds are evaluated). */
    isCurrentRound() {
      return (
        this.isHostInstructionMode || this.myLatestRoundNumber === this.parentCounterpoint?.round
      );
    },

    /** The current annotation is a host instruction and the UI permissions are set for organization host */
    isHostInstructionMode() {
      return (
        this.parent.type === 'hostInstruction' && this.permissionLevel === PermissionLevel.HOST
      );
    },

    citationLinkProps() {
      const isCitationLoaded = this.citation.disputed;
      const isCitationEditable = this.isCitationEditable(this.citation);

      if (isCitationEditable && !isCitationLoaded) {
        return {
          tooltip: 'Click to review citation',
          click: () => this.emitEventBusAction('citation.edit'),
        };
      }

      if (isCitationLoaded || this.publicView) {
        return {
          tooltip: 'Click to view citation',
          click: () => this.emitEventBusAction('citation.readOnly'),
        };
      }

      const isCurrentTeam = this.parentCounterpoint?.team?._id === this.teamID;
      const isTeamLead = this.myLatestRound?.author?._id === this.userId;

      if (this.myLatestRoundActive && isTeamLead && !isCurrentTeam) {
        return {
          tooltip: 'Click to review citation',
          click: () => this.emitEventBusAction('citation.review'),
        };
      }

      // default action
      return {
        tooltip: 'Click to view citation',
        click: () => this.emitEventBusAction('citation.readOnly'),
      };
    },
    isFile() {
      return this.citation.url.endsWith('.docx') || this.citation.url.endsWith('.txt');
    },
    islink() {
      return url(this.citation.url);
    },
    isCite() {
      return this.citation.url.includes('debate');
    },
  },

  methods: {
    /**
     * Is Citation Editable
     */
    isCitationEditable() {
      const isRoundPublished =
        this.parentCounterpoint && this.roundPublished(this.parentCounterpoint?.round);
      if (isRoundPublished) return false;

      if (
        this.parentCounterpoint?.round === this.debateNextState?.current_round &&
        this.debateNextState?.current_status === 'Published'
      )
        return false;

      const isCitationAuthor = this.citation?.createdBy?._id === this.userId;
      const isTeamLead = this.myLatestRound?.author?._id === this.userId;
      const isRoundFinalized = this.roundStatus === RoundStatus.FINALIZING;
      //
      // Team Lead validations
      if (isTeamLead) {
        if (isCitationAuthor) return true;

        if (isRoundFinalized) {
          if (
            this.isInstruction || // <=======================================================
            this.isAnnotationApproved // <=======================================================
          )
            return true;
        }

        return false;
      }
      //
      // Team Member validations
      if (isCitationAuthor) {
        const isAnnotationsSubmitted = this.teamMemberData?.submitted;
        if (!isRoundFinalized && !isAnnotationsSubmitted) return true;
      }

      return false;
    },

    emitEventBusAction(event) {
      EventBus.emit(event, this.citation, this.parent, this.citation.parentType);
    },

    /**
     * Action for Delete Citation button
     * Emits 'citation.remove' event on global event-bus
     */
    removeCitationClicked() {
      this.emitEventBusAction('citation.remove');
    },
  },
};
</script>
